/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'jquery',
	'underscore',
	'text!../../templates/FormatDialog.template',
	'doT',
	'react-dom',
	'react',
	'./FormatProperties',
	'./FormatTypes',
	'./FormatConstants'
], function(View, $, _, FormatDialogTemplate, dot, ReactDOM, React, FormatProperties, FormatTypes, FormatConstants) {

	/**
	 * @classdesc  Creates a format dialog
	 * @public
	 *
	 * @param {object} options.eventHandler - Event handler
	 * @param {object} options.getMetaDataFormat - Function that returns the metadata format of the data item
	 * @param {String} options._itemLabel - Label of the data item
	 * @param {object} options.formatHelper - Object containing details from shaping for format types and properties
	 * @param {function} options.getDataItemFormat -- A callback function that will execute and return the current data Item format, so that dialog reflects this format option
	 */
	var FormatView = View.extend({

		init: function(options) {
			// Setup dimensions for OverlayView base class
			_.extend(this, options.state);

			FormatView.inherited('init', this, arguments);
			this._selectionHandler = options.actions.apply;
			this._getMetadataFormat = options.state.getMetadataFormat;
			this._itemLabel = options.state.itemLabel ? options.state.itemLabel : '';
			this._suppressSelectionLabel = options.state.suppressSelectionLabel || false;

			// iWidth is the sum of full width of a field + the paddings + the format type width
			this._iWidth = '590';
			this._iHeight = '570';
			if (this._suppressSelectionLabel) {
				this._iHeight = '530';
			}

			if (options.state) {
				const visFormat = options.state.format && options.state.format.formatSpec;
				if (visFormat && options.state.visScope) {
					// Check exists vis format. If dialog is opening with visScope meaning open format dialog at viz level.
					this.state = this._getDefaultFormat(visFormat);
				} else {
					// The format dialog is opened from a data item in slot
					if (options.state.getDataItemFormat) {
						this.state = this._getDefaultFormat(options.state.getDataItemFormat());
					} else {
						this.state = {};
					}
					if (visFormat) {
						// TODO need to clarify and clean the usage of 'local', but it's too risky to remove now.
						// Please check how FormatProperties.getMissingValuesOptions is called.
						// vis format is stored in state.properties during rendering, and flag 'local' is required. 
						Object.assign(this.state.properties, options.state.format.formatSpec);
						this.state['local'] = this.state['local'] || visFormat.local;
					}
				}
			}

			this.initialState = Object.assign({}, this.state);
			this.dataType = options.state.dataType;
			this._formatHelper = options.state.formatHelper;
			this.disabledItems = options.state.getDisabledItems ? options.state.getDisabledItems() : [];
			this._visScope = options.state.visScope || false;
			this.mountNode = null;
		},

		/**
		 * Translates the format spec into a spec consumable/more manageable by the dialog
		 * ie: {type: number, local: false, properties: {maximumFractionDigits: 2}}
		 * @param {object} - A format spec
		 * @returns {object} - A format spec consumable by the dialog
		 */
		_getDefaultFormat: function(format) {
			var spec = {};
			format = format && format.formatSpec ? format.formatSpec : format;
			if (format) {
				spec.type = format.type ? format.type : FormatConstants.FORMAT_TYPES.NUMBER;
				spec.local = format.local ? format.local : false;
				spec.properties = _.omit(format, ['type', 'local']);
			}
			return spec;
		},

		render: function() {
			var dialogHtml = dot.template(FormatDialogTemplate)({
				itemLabel: this._itemLabel,
				suppressSelectionLabel: this._suppressSelectionLabel
			});
			this.$el
				.addClass('popoverFormatDialogContainer formatDialog')
				.height(this._iHeight + 'px')
				.width(this._iWidth + 'px')
				.html(dialogHtml);
			return Promise.resolve();
		},

		/**
		 * The focus is handled in FormatTypes, this is here to suppress the focus on the back button.
		 */
		setFocus: function() {},

		_renderCommonView: function(resolve) {
			//TODO: Add prop validation
			/* eslint-disable react/prop-types */
			class FormatDialog extends React.Component {
				constructor(props) {
					super(props);
					this.state = props.state;
					this.initialState = props.initialState;
					this.formatHelper = props.formatHelper;
					this.specCache = this.state.properties;
					this.dialogNode = props.dialogNode;
					this.disabledItems = props.disabledItems;
					this.autoDescription = props.autoDescription;
					this.defaultDescription = props.defaultDescription;
					this.dataType = props.dataType;
					this.visScope = props.visScope;
				}
				onChange() {
					this.props.onFormatChange(this.state);
				}
				onTypeChange(spec) {
					spec.properties = this.specCache || {};
					this.setState(spec, this.onChange);
				}
				onPropertyChange(spec) {
					var state = this.state;
					_.extend(state.properties, spec);
					_.extend(this.specCache, state.properties);
					this.setState(state);
					this.onChange();
				}
				render() {
					const types = <FormatTypes
						state={this.state}
						initialState={this.initialState}
						onChange={this.onTypeChange.bind(this)}
						definition={this.formatHelper.types}
						dataType={this.dataType}
						visScope={this.visScope}
					/>;
					const properties = <FormatProperties
						state={this.state}
						disabledItems={this.disabledItems}
						onChange={this.onPropertyChange.bind(this)}
						definition={this.formatHelper.properties}
						dialogNode={this.dialogNode}
						autoDescription={this.autoDescription}
						defaultDescription={this.defaultDescription}
					/>;
					return (
						<div className='formatView'>
							{[types, properties]}
						</div>
					);
				}
			}
			/* eslint-enable react/prop-types */

			const view = <FormatDialog
				state={this.state}
				initialState={this.initialState}
				onFormatChange={this._onFormatChange.bind(this)}
				disabledItems={this.disabledItems}
				getMetadataSpec={this._getMetadataSpec.bind(this)}
				formatHelper={this._formatHelper}
				dialogNode={this.$el[0]}
				autoDescription= {this.autoDescription}
				defaultDescription= {this.defaultDescription}
				dataType={this.dataType}
				visScope={this._visScope}
			/>;

			this.mountNode = this.$el.find('.formatContent')[0];

			ReactDOM.render(view, this.mountNode, resolve);
		},

		remove: function() {
			if (this.mountNode) {
				ReactDOM.unmountComponentAtNode(this.mountNode);
			}
		},

		_getMetadataSpec: function() {
			return this._getDefaultFormat(this._getMetadataFormat());
		},

		_onFormatChange: function(spec) {
			this.state = spec;
			this._selectionHandler(spec);
		},

		notifyRenderComplete: function(){
			return new Promise((resolve, reject) => {
				try {
					this._renderCommonView(resolve);
				} catch(error) {
					reject(error);
				}
			});
		}
	});
	return FormatView;
});
